import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type TopPage } from '../api.ts';

type Filter = 'all' | TopPage['kind'];

const KIND_LABEL: Record<TopPage['kind'], string> = {
  home: 'Home',
  category: 'Category',
  article: 'Article',
  roadmap: 'Roadmap',
  stats: 'Stats',
  other: 'Other',
};

export function StatsPage(): JSX.Element {
  const [pages, setPages] = useState<TopPage[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    api.topPages(200).then(d => setPages(d.pages));
  }, []);

  const filtered = useMemo(() => {
    if (!pages) return [];
    if (filter === 'all') return pages;
    return pages.filter(p => p.kind === filter);
  }, [pages, filter]);

  if (!pages) return <div className="center-pad"><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  const totalViews = pages.reduce((n, p) => n + p.views, 0);
  const totalUniques = pages.reduce((n, p) => n + p.uniques, 0);
  const maxViews = pages.length > 0 ? Math.max(...pages.map(p => p.views)) : 1;

  const counts: Record<Filter, number> = {
    all: pages.length,
    home: pages.filter(p => p.kind === 'home').length,
    article: pages.filter(p => p.kind === 'article').length,
    category: pages.filter(p => p.kind === 'category').length,
    roadmap: pages.filter(p => p.kind === 'roadmap').length,
    stats: pages.filter(p => p.kind === 'stats').length,
    other: pages.filter(p => p.kind === 'other').length,
  };

  const tabs: Filter[] = ['all', 'article', 'category', 'home', 'roadmap', 'stats'];

  return (
    <div className="page">
      <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-faint)' }}>
        <Link to="/">Home</Link> / Stats
      </div>
      <h1 style={{ fontSize: 'clamp(28px, 4vw, 38px)', margin: '0 0 8px', letterSpacing: '-.02em' }}>Top pages</h1>
      <p style={{ color: 'var(--text-dim)', maxWidth: 700, fontSize: 17, marginBottom: 18 }}>
        Pages ranked by total views. One-shot bots and crawlers are filtered out — only browsers
        that returned at least once are counted.
      </p>

      {pages.length > 0 && (
        <div className="stats" style={{ marginBottom: 8 }}>
          <span><strong>{pages.length}</strong> pages tracked</span>
          <span><strong>{totalViews.toLocaleString()}</strong> total views</span>
          <span><strong>{totalUniques.toLocaleString()}</strong> total unique browsers</span>
        </div>
      )}

      <div className="roadmap-tabs">
        {tabs.map(t => (
          <button key={t} className={filter === t ? 'on' : ''} onClick={() => setFilter(t)}>
            {t === 'all' ? 'All' : KIND_LABEL[t]} ({counts[t]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="center-pad" style={{ padding: '40px 20px' }}>
          No views recorded yet for this filter.
        </div>
      ) : (
        <div style={{ marginTop: 18, background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 14 }}>
          <div className="stats-row stats-head">
            <span>#</span>
            <span>Page</span>
            <span className="num">Views</span>
            <span className="num">Unique</span>
            <span>Share</span>
          </div>
          {filtered.map((p, i) => (
            <div className="stats-row" key={p.path}>
              <span className="rank">{i + 1}</span>
              <div className="page-cell">
                <PageLink page={p} />
                <div className="path">{p.path}</div>
              </div>
              <span className="num"><strong>{p.views.toLocaleString()}</strong></span>
              <span className="num">{p.uniques.toLocaleString()}</span>
              <span className="bar-wrap" aria-hidden>
                <span className="bar" style={{ width: `${Math.max(2, (p.views / maxViews) * 100)}%` }} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PageLink({ page }: { page: TopPage }): JSX.Element {
  const label = `${KIND_LABEL[page.kind]}: ${page.title}`;
  if (page.kind === 'other') return <span>{label}</span>;
  return <Link to={page.path}>{label}</Link>;
}
